import * as React from 'react'
import Popup from 'reactjs-popup'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ResourcesOverviewTagShape, UserShape } from '../../api/api_data_types'
import { upsertResourcesOverviewCellTag } from '../../api/resources_overview_cell_tags'
import { getResourcesOverviewTags } from '../../api/resources_overview_tags'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'
import { getTextColorForBackground } from '../helpers/color'
import { separateTime } from '../helpers/time'

type CellTagPopupProps = {
  open: boolean
  user: UserShape | null
  date: string | null
  tag: ResourcesOverviewTagShape | null
  externalInstallerTime: number | null
  onClose: () => void
  onMutationStart?: () => void
  onMutationEnd?: () => void
}

type CacheUpdateOptions = {
  nextTag?: ResourcesOverviewTagShape | null
  nextExternalInstallerTime?: number | null
}

type SavePayload = {
  user_id: number
  date: string
  tag_id?: number | null
  time_minutes?: number
}

export default function CellTagPopup({
  open,
  user,
  date,
  tag,
  externalInstallerTime,
  onClose,
  onMutationStart,
  onMutationEnd,
}: CellTagPopupProps) {
  const queryClient = useQueryClient()
  const [selectedTag, setSelectedTag] = React.useState<ResourcesOverviewTagShape | null>(null)
  const [hours, setHours] = React.useState(0)
  const [minutes, setMinutes] = React.useState(0)

  const { data, isLoading, isError, error } = useQuery(['resources_overview_tags'], () => getResourcesOverviewTags())
  const tags = data?.data || []
  const isExternalInstaller = Boolean(user?.external_installer)

  React.useEffect(() => {
    if (!open) return

    setSelectedTag(tag || null)
    const [nextHours, nextMinutes] = separateTime(externalInstallerTime || 0)
    setHours(nextHours)
    setMinutes(nextMinutes)
  }, [open, externalInstallerTime, tag, user?.id])

  const updateCellTagInOverviewCache = ({ nextTag, nextExternalInstallerTime }: CacheUpdateOptions) => {
    if (!user || !date) return

    const username = (user.username || '').toUpperCase()
    if (!username) return

    queryClient.setQueriesData({ queryKey: ['resources_overview'] }, (current: any) => {
      if (!current?.data?.[username]?.[date]) return current

      const userRow = current.data[username]
      const cell = userRow[date]
      const updatedCell = { ...cell }
      if (nextTag !== undefined) {
        updatedCell.tag = nextTag ? { id: nextTag.id, name: nextTag.name, color: nextTag.color } : null
      }
      if (nextExternalInstallerTime !== undefined) {
        updatedCell.external_installer_time = nextExternalInstallerTime
      }

      return {
        ...current,
        data: {
          ...current.data,
          [username]: {
            ...userRow,
            [date]: updatedCell,
          },
        },
      }
    })
  }

  const saveMutation = useMutation(upsertResourcesOverviewCellTag)
  const mutationError = saveMutation.error
  const mutationLoading = saveMutation.isLoading

  const closePopup = () => {
    saveMutation.reset()
    onClose()
  }

  const clamp = (value: number, min: number, max: number) => {
    if (Number.isNaN(value)) return min
    return Math.min(max, Math.max(min, value))
  }

  const normalizedSubmittedTimeMinutes = React.useMemo(() => {
    const normalizedHours = clamp(hours, 0, 23)
    const normalizedMinutes = clamp(minutes, 0, 59)
    return normalizedHours * 60 + normalizedMinutes
  }, [hours, minutes])

  const pendingExternalInstallerTime = normalizedSubmittedTimeMinutes === 0 ? null : normalizedSubmittedTimeMinutes
  const hasTagChanged = (selectedTag?.id || null) !== (tag?.id || null)
  const hasTimeChanged = isExternalInstaller && pendingExternalInstallerTime !== (externalInstallerTime ?? null)

  const onSave = () => {
    if (!user || !date) return

    if (!hasTagChanged && !hasTimeChanged) {
      closePopup()
      return
    }

    const previousTag = tag || null
    const previousExternalInstallerTime = externalInstallerTime ?? null
    const payload: SavePayload = {
      user_id: user.id,
      date,
    }
    if (hasTagChanged) {
      payload.tag_id = selectedTag ? selectedTag.id : null
    }
    if (hasTimeChanged && isExternalInstaller) {
      payload.time_minutes = normalizedSubmittedTimeMinutes
    }

    onMutationStart?.()
    updateCellTagInOverviewCache({
      nextTag: hasTagChanged ? selectedTag : undefined,
      nextExternalInstallerTime: hasTimeChanged ? pendingExternalInstallerTime : undefined,
    })
    saveMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries('resources_overview')
        onClose()
      },
      onError: () => {
        updateCellTagInOverviewCache({
          nextTag: hasTagChanged ? previousTag : undefined,
          nextExternalInstallerTime: hasTimeChanged ? previousExternalInstallerTime : undefined,
        })
      },
      onSettled: () => {
        onMutationEnd?.()
      },
    })
  }

  return (
    <Popup
      open={open}
      onClose={closePopup}
      position="center center"
      contentStyle={{ width: 'auto', maxWidth: '100%', minWidth: '420px', zIndex: 9999 }}
      overlayStyle={{ zIndex: 9998 }}
      modal
      nested
      closeOnDocumentClick={false}
    >
      <div className="modal-popup resources-overview-cell-tag-popup">
        <a className="close" onClick={closePopup}>
          &times;
        </a>
        <div className="popup-header">
          {user && date && (
            <div className="text-muted">
              {user.name} ({user.username}) - {date}
            </div>
          )}
        </div>

        <div className="popup-content">
          {isLoading && (
            <div className="text-center">
              <i className="fa fa-spinner fa-spin" />
            </div>
          )}

          {!isLoading && !isError && (
            <div className="mb-3">
              {tags.map((availableTag: ResourcesOverviewTagShape) => {
                const isSelected = selectedTag?.id === availableTag.id
                return (
                  <div key={availableTag.id} className="form-check mb-2 d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="resources-overview-tag"
                      id={`resources-overview-tag-${availableTag.id}`}
                      disabled={mutationLoading}
                      checked={isSelected}
                      onChange={() => setSelectedTag(availableTag)}
                    />
                    <label className="form-check-label pl-1" htmlFor={`resources-overview-tag-${availableTag.id}`}>
                      <span
                        className="badge badge-pill border px-3 py-2"
                        style={{
                          backgroundColor: availableTag.color,
                          color: getTextColorForBackground(availableTag.color),
                          borderColor: 'rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        {availableTag.name}
                      </span>
                    </label>
                  </div>
                )
              })}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="resources-overview-tag"
                  id="resources-overview-tag-none"
                  disabled={mutationLoading}
                  checked={!selectedTag}
                  onChange={() => setSelectedTag(null)}
                />
                <label className="form-check-label" htmlFor="resources-overview-tag-none">
                  Ingen tag
                </label>
              </div>
              {tags.length === 0 && <span className="text-muted font-italic">Ingen tags</span>}
            </div>
          )}

          {isExternalInstaller && (
            <div className="mb-3">
              <label className="d-block mb-2">Manuel tid</label>
              <div className="form-row">
                <div className="col-6">
                  <label className="mb-1 font-weight-bold" htmlFor="resources-overview-time-hours">Timer</label>
                  <input
                    id="resources-overview-time-hours"
                    type="number"
                    min={0}
                    max={23}
                    className="form-control"
                    value={hours}
                    disabled={mutationLoading}
                    onChange={(event) => {
                      setHours(clamp(parseInt(event.target.value, 10), 0, 23))
                    }}
                  />
                </div>
                <div className="col-6">
                  <label className="mb-1 font-weight-bold" htmlFor="resources-overview-time-minutes">Minutter</label>
                  <input
                    id="resources-overview-time-minutes"
                    type="number"
                    min={0}
                    max={59}
                    className="form-control"
                    value={minutes}
                    disabled={mutationLoading}
                    onChange={(event) => {
                      setMinutes(clamp(parseInt(event.target.value, 10), 0, 59))
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <ErrorMessage isError={Boolean(isError)} error={error} className="error-message" />
          <ErrorMessage isError={Boolean(mutationError)} error={mutationError} className="error-message" />
        </div>

        <div className="popup-actions d-flex justify-content-end align-items-center">
          <div className="d-flex">
            <button type="button" className="btn btn-outline-secondary btn-sm mr-2" onClick={closePopup} disabled={mutationLoading}>
              Annuller
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onSave}
              disabled={mutationLoading || (!hasTagChanged && !hasTimeChanged)}
            >
              Gem
            </button>
          </div>
        </div>
      </div>
    </Popup>
  )
}
